import { StyleSheet, View } from "react-native";

import {
  ActivityIndicator,
  Button,
  PanelHeaderTitleWithStatusBadgeProps,
  Typography,
  VStack,
} from "@smartrent/ui";

import { PanelCard } from "@/common/PanelCard";
import { useDrawerNav } from "@/common/AppDrawer";
import { useOrganization } from "@/pages/organization/OrganizationContext";
import { useSsoSetupQuery } from "@/api/sso-setup";
import { formatDate } from "@/utils/date";

type StatusBadge = Pick<
  PanelHeaderTitleWithStatusBadgeProps,
  "status" | "statusLabel"
>;

type SsoSetupStatus = "configured" | "pending" | "not_configured";

// `PanelHeaderTitleWithStatusBadge` only accepts success/error/warning/knowledge,
// so `knowledge` stands in for the neutral "default" badge here.
const STATUS_BADGES: Record<SsoSetupStatus, StatusBadge> = {
  configured: { status: "success", statusLabel: "SSO configured via Auth0" },
  pending: { status: "warning", statusLabel: "SSO setup pending — link sent" },
  not_configured: { status: "knowledge", statusLabel: "Not configured" },
};

const LOADING_BADGE: StatusBadge = {
  status: "knowledge",
  statusLabel: "Loading…",
};

// Never fall back to "Not configured" on a failed fetch — that would be a silent
// lie about the organization's actual state.
const ERROR_BADGE: StatusBadge = {
  status: "error",
  statusLabel: "Status unavailable",
};

export const OrganizationSsoCard = () => {
  const { organization } = useOrganization();
  const drawer = useDrawerNav();

  const { data, isLoading, isError } = useSsoSetupQuery({
    organizationId: organization.id,
  });

  const connectionName = data?.auth0_connection_name?.trim() || null;
  const ticket = data?.ticket ?? null;

  // A connection name outranks a sent link: it is only captured once the client has
  // finished Auth0's wizard, so CMW's webhook is the source of truth even when a
  // setup link has gone out and not been used.
  const status: SsoSetupStatus = connectionName
    ? "configured"
    : ticket
      ? "pending"
      : "not_configured";

  const statusBadge = isError
    ? ERROR_BADGE
    : isLoading
      ? LOADING_BADGE
      : STATUS_BADGES[status];

  return (
    <PanelCard title="SSO Setup" statusBadge={statusBadge}>
      <VStack>
        <VStack spacing={4} style={styles.body}>
          {isLoading ? (
            <ActivityIndicator />
          ) : isError ? (
            <Typography color="textSecondary">
              Could not load SSO setup status for this organization.
            </Typography>
          ) : status === "configured" ? (
            <>
              <Typography type="title5" color="textSecondary">
                Auth0 Connection
              </Typography>
              <Typography>{connectionName}</Typography>
            </>
          ) : status === "pending" ? (
            <>
              <Typography>
                {`Generated by ${ticket?.created_by ?? "an unknown account"} on ${formatDate(
                  { date: ticket?.created_at }
                )}`}
              </Typography>
              <Typography color="textSecondary">
                The link is not stored and cannot be shown again — generate a
                new one if the client needs it.
              </Typography>
            </>
          ) : (
            <Typography color="textSecondary">
              This organization does not have an Auth0 enterprise connection.
            </Typography>
          )}
        </VStack>

        {/* CARD FOOTER */}
        <View style={styles.footer}>
          <Button
            variation="plain"
            onPress={() => drawer.push("ssoSetup", { organization })}
          >
            Manage SSO Setup
          </Button>
        </View>
      </VStack>
    </PanelCard>
  );
};

const styles = StyleSheet.create({
  body: {
    padding: 12,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "center",
    paddingBottom: 12,
  },
});
